import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { HISTORY_PAGE_SIZE, currencyFormatter } from '../../application/inventoryUtils';
import { useInventoryHistoryUiState } from '../../application/inventoryHistoryUiState';
import {
  buildStockMovementHistoryCsv,
  extractSaleIdFromStockMovement,
  extractWorkOrderIdFromStockMovement,
  getStockMovementReasonLabel,
  type StockMovementRow,
  type StockRow,
} from '../../../inventory/stock/public-api';
import { openSaleDetail } from '../../../sales/public-api';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { shareOrDownloadCsv } from '../../../shared/csv-core/infra/shareCsv';
import { useEmployeesStore } from '../../../team/store/useEmployeesStore';
import { useInventoryUiStore } from '../../store/index';
import { useInventoryOperationsController } from './useInventoryOperationsController';

type Params = {
  ctx: {
    stockHistoryRemoteOptions?: import('../../application/inventoryOperations.types').StockMovementHistoryRemoteOptions;
    currentBranchName: string;
    actorLabel: (uid: string | null | undefined) => string;
  };
};

const HISTORY_LOAD_ERROR = 'No pudimos cargar el historial. Revisá tu conexión y reintentá.';

export function useInventoryHistoryFlow({ ctx }: Params) {
  const controller = useInventoryOperationsController();
  const navigation = useNavigation();
  const [historyReasonFilter, setHistoryReasonFilter] = useState<string>('all');
  const [historyFromDate, setHistoryFromDate] = useState('');
  const [historyToDate, setHistoryToDate] = useState('');
  const [exportingHistory, setExportingHistory] = useState(false);

  // #868: el historial completo de movimientos del equipo es owner-only.
  const canViewTeamHistory = ctx.stockHistoryRemoteOptions?.useRemoteTeamHistory === true;

  const {
    historySelectedStock,
    historyVisible,
    setHistorySelectedStock,
    setHistoryVisible,
    setHistoryLoading,
    setMovementHistory,
    historyOffset,
    setHistoryOffset,
    historyHasMore,
    setHistoryHasMore,
    historyLoadingMore,
    setHistoryLoadingMore,
  } = useInventoryUiStore();

  const {
    setHistoryError,
    setHistoryOwnerOnly,
    setRetryHistoryLoad,
    resetHistoryUiExtras,
  } = useInventoryHistoryUiState();

  const historyFilterForm = useMemo(
    () => ({ historyReasonFilter, historyFromDate, historyToDate }),
    [historyFromDate, historyReasonFilter, historyToDate]
  );

  const historyReasonOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'ingresos', label: 'Ingresos' },
      { value: 'egresos', label: 'Egresos' },
      { value: 'ajustes', label: 'Ajustes' },
      { value: 'transferencias', label: 'Transferencias' },
    ],
    []
  );

  const applyHistoryFilters = useCallback(async () => {
    if (!historySelectedStock) return;
    if (!canViewTeamHistory) {
      // Owner-only: no pegarle al servidor; el modal muestra el estado explícito.
      setMovementHistory([]);
      setHistoryHasMore(false);
      return;
    }
    const gen = ++filterGenerationRef.current;
    setHistoryLoading(true);
    setHistoryError(null);
    setHistoryOffset(0);
    setHistoryHasMore(false);
    setHistoryLoadingMore(false);
    try {
      const rows = await controller.loadMovementHistoryPage(
        historySelectedStock,
        HISTORY_PAGE_SIZE,
        0,
        historyFilterForm,
        ctx.stockHistoryRemoteOptions
      );
      if (gen !== filterGenerationRef.current) return;
      setMovementHistory(rows);
      setHistoryOffset(rows.length);
      setHistoryHasMore(rows.length === HISTORY_PAGE_SIZE);
    } catch (error) {
      if (gen !== filterGenerationRef.current) return;
      console.warn('[Inventory] No se pudo cargar el historial de movimientos', error);
      setMovementHistory([]);
      setHistoryError(HISTORY_LOAD_ERROR);
    } finally {
      if (gen === filterGenerationRef.current) setHistoryLoading(false);
    }
  }, [
    canViewTeamHistory,
    controller,
    ctx.stockHistoryRemoteOptions,
    historyFilterForm,
    historySelectedStock,
    setHistoryError,
    setHistoryHasMore,
    setHistoryLoading,
    setHistoryLoadingMore,
    setHistoryOffset,
    setMovementHistory,
  ]);

  // El modal pide retry a través del estado extra (sin prop nueva en el call site).
  useEffect(() => {
    setRetryHistoryLoad(() => {
      void applyHistoryFilters();
    });
  }, [applyHistoryFilters, setRetryHistoryLoad]);

  // Re-fetch when filters change while modal is open (skip initial load — openHistoryModal handles that)
  const isFirstFilterRun = useRef(true);
  const filterGenerationRef = useRef(0);
  useEffect(() => {
    if (!historyVisible) {
      isFirstFilterRun.current = true;
      return;
    }
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    void applyHistoryFilters();
  }, [historyFilterForm, historyVisible, applyHistoryFilters]);

  const openHistoryModal = useCallback(
    async (stock: StockRow) => {
      const gen = ++filterGenerationRef.current;
      setHistorySelectedStock(stock);
      setHistoryVisible(true);
      setHistoryOffset(0);
      setHistoryHasMore(false);
      setHistoryLoadingMore(false);
      setHistoryReasonFilter('all');
      setHistoryFromDate('');
      setHistoryToDate('');
      setHistoryError(null);
      setHistoryOwnerOnly(!canViewTeamHistory);
      setMovementHistory([]);
      void useEmployeesStore.getState().loadEmployees().catch(() => {});
      if (!canViewTeamHistory) {
        // Owner-only (#868): estado explícito en el modal, sin fetch.
        setHistoryLoading(false);
        return;
      }
      setHistoryLoading(true);
      try {
        const rows = await controller.loadMovementHistoryPage(
          stock,
          HISTORY_PAGE_SIZE,
          0,
          { historyReasonFilter: 'all', historyFromDate: '', historyToDate: '' },
          ctx.stockHistoryRemoteOptions
        );
        if (gen !== filterGenerationRef.current) return;
        setMovementHistory(rows);
        setHistoryOffset(rows.length);
        setHistoryHasMore(rows.length === HISTORY_PAGE_SIZE);
      } catch (error) {
        if (gen !== filterGenerationRef.current) return;
        console.warn('[Inventory] No se pudo cargar el historial de movimientos', error);
        setMovementHistory([]);
        setHistoryError(HISTORY_LOAD_ERROR);
      } finally {
        if (gen === filterGenerationRef.current) setHistoryLoading(false);
      }
    },
    [
      canViewTeamHistory,
      controller,
      ctx.stockHistoryRemoteOptions,
      setHistoryError,
      setHistoryOwnerOnly,
      setHistoryHasMore,
      setHistoryLoading,
      setHistoryLoadingMore,
      setHistoryOffset,
      setHistorySelectedStock,
      setHistoryVisible,
      setMovementHistory,
    ]
  );

  const loadMoreHistory = useCallback(async () => {
    if (!historySelectedStock || historyLoadingMore || !historyHasMore || !canViewTeamHistory) return;
    setHistoryLoadingMore(true);
    try {
      const rows = await controller.loadMovementHistoryPage(
        historySelectedStock,
        HISTORY_PAGE_SIZE,
        historyOffset,
        historyFilterForm,
        ctx.stockHistoryRemoteOptions
      );
      setMovementHistory((current: StockMovementRow[]) => [...current, ...rows]);
      setHistoryOffset((current: number) => current + rows.length);
      setHistoryHasMore(rows.length === HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Inventory] No se pudieron cargar más movimientos', error);
      setHistoryHasMore(false);
      showError('Historial', 'No pudimos cargar más movimientos. Reintentá en un momento.');
    } finally {
      setHistoryLoadingMore(false);
    }
  }, [
    canViewTeamHistory,
    controller,
    ctx.stockHistoryRemoteOptions,
    historyFilterForm,
    historyHasMore,
    historyLoadingMore,
    historyOffset,
    historySelectedStock,
    setHistoryHasMore,
    setHistoryLoadingMore,
    setHistoryOffset,
    setMovementHistory,
  ]);

  // get_work_order exige la capability 'work_orders' en el servidor; sin ella
  // navegar al detalle devuelve 403 (callejón sin salida). Guard doble: el
  // modal ya oculta el link, esto cubre cualquier otro caller.
  const workOrdersEnabled = useHasBusinessCapability('work_orders');

  /** Abre la referencia del movimiento: venta (sale/sale_void) u orden de trabajo. */
  const openSaleFromMovement = useCallback(
    (movement: StockMovementRow) => {
      const saleId = extractSaleIdFromStockMovement({ reason: movement.reason, referenceId: movement.referenceId });
      if (saleId) {
        openSaleDetail(navigation, { saleId });
        return;
      }
      const workOrderId = workOrdersEnabled
        ? extractWorkOrderIdFromStockMovement({
            reason: movement.reason,
            referenceId: movement.referenceId,
          })
        : null;
      if (workOrderId) {
        navigateInShell(navigation, 'WorkOrderDetail', { workOrderId });
      }
    },
    [navigation, workOrdersEnabled]
  );

  const exportMovementHistoryCsv = useCallback(async () => {
    if (!historySelectedStock || exportingHistory) return;
    if (!canViewTeamHistory) {
      // Owner-only (#868): explicar en vez de exportar un CSV vacío en silencio.
      showInfo(
        'Historial del equipo',
        'El historial completo lo descarga el dueño de la organización.'
      );
      return;
    }
    setExportingHistory(true);
    try {
      const rows = await controller.loadMovementHistoryExport(
        historySelectedStock,
        historyFilterForm,
        10_000,
        ctx.stockHistoryRemoteOptions
      );
      if (rows.length === 0) {
        showInfo('Sin movimientos', 'No hay movimientos para exportar con esos filtros.');
        return;
      }
      const csv = buildStockMovementHistoryCsv({
        productName: historySelectedStock.productName,
        branchLabel: ctx.currentBranchName,
        unitOfMeasure: historySelectedStock.unitOfMeasure,
        rows,
        reasonLabel: getStockMovementReasonLabel,
        actorLabel: ctx.actorLabel,
        formatMoney: (n) => (n != null ? currencyFormatter.format(n) : ''),
      });
      const safeName = historySelectedStock.productName.replace(/[^\w\-]+/g, '_').slice(0, 40);
      await shareOrDownloadCsv(csv, `historial_stock_${safeName}.csv`);
    } catch (e) {
      console.warn('[Inventory] export CSV', e);
      showError('Exportar', 'No pudimos generar el archivo. Reintentá.');
    } finally {
      setExportingHistory(false);
    }
  }, [canViewTeamHistory, controller, ctx.actorLabel, ctx.currentBranchName, ctx.stockHistoryRemoteOptions, exportingHistory, historyFilterForm, historySelectedStock]);

  const closeHistoryModal = useCallback(() => {
    setHistoryVisible(false);
    setMovementHistory([]);
    setHistoryOffset(0);
    setHistoryHasMore(false);
    setHistoryReasonFilter('all');
    setHistoryFromDate('');
    setHistoryToDate('');
    resetHistoryUiExtras();
  }, [resetHistoryUiExtras, setHistoryHasMore, setHistoryOffset, setHistoryVisible, setMovementHistory]);

  return {
    historyReasonFilter,
    setHistoryReasonFilter,
    historyFromDate,
    setHistoryFromDate,
    historyToDate,
    setHistoryToDate,
    historyReasonOptions,
    openHistoryModal,
    loadMoreHistory,
    closeHistoryModal,
    applyHistoryFilters,
    exportMovementHistoryCsv,
    exportingHistory,
    openSaleFromMovement,
  };
}
